import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { CheckCheck, Trash2, Tag, FolderOpen } from 'lucide-react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import { useConfirm } from '@/hooks/useConfirm'

interface BlogPost {
  id: string
  title: string
  status: 'draft' | 'review' | 'published' | 'scheduled'
}

interface BulkOperationsToolbarProps {
  selectedPosts: BlogPost[]
  categories: Array<{ id: string; name: string }>
  tags: Array<{ id: string; name: string }>
  onComplete: () => void
}

export function BulkOperationsToolbar({
  selectedPosts,
  categories,
  tags,
  onComplete,
}: BulkOperationsToolbarProps) {
  const { confirm, ConfirmDialog } = useConfirm()
  const [loading, setLoading] = useState(false)
  const selectedCount = selectedPosts.length

  if (selectedCount === 0) return null

  const handleBulkPublish = async () => {
    try {
      setLoading(true)
      const response = await apiService.blog.bulk.publish({
        post_ids: selectedPosts.map((p) => p.id),
      })
      toast.success(response.message)
      onComplete()
    } catch (error) {
      toast.error('Failed to publish posts')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleBulkUnpublish = async () => {
    try {
      setLoading(true)
      const response = await apiService.blog.bulk.unpublish({
        post_ids: selectedPosts.map((p) => p.id),
      })
      toast.success(response.message)
      onComplete()
    } catch (error) {
      toast.error('Failed to unpublish posts')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleBulkStatusChange = async (newStatus: string) => {
    try {
      setLoading(true)
      const response = await apiService.blog.bulk.updateStatus({
        post_ids: selectedPosts.map((p) => p.id),
        new_status: newStatus,
      })
      toast.success(response.message)
      onComplete()
    } catch (error) {
      toast.error('Failed to update post status')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleBulkDelete = () => {
    confirm({
      title: 'Delete Posts',
      message: `Are you sure you want to delete ${selectedCount} post(s)? This action cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          setLoading(true)
          const response = await apiService.blog.bulk.delete({
            post_ids: selectedPosts.map((p) => p.id),
          })
          toast.success(response.message)
          onComplete()
        } catch (error) {
          toast.error('Failed to delete posts')
          console.error(error)
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const handleBulkCategoryAssign = async (categoryId: string, replace: boolean) => {
    try {
      setLoading(true)
      const response = await apiService.blog.bulk.assignCategories({
        post_ids: selectedPosts.map((p) => p.id),
        category_ids: [categoryId],
        replace,
      })
      toast.success(response.message)
      onComplete()
    } catch (error) {
      toast.error('Failed to assign categories')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleBulkTagAssign = async (tagId: string, replace: boolean) => {
    try {
      setLoading(true)
      const response = await apiService.blog.bulk.assignTags({
        post_ids: selectedPosts.map((p) => p.id),
        tag_ids: [tagId],
        replace,
      })
      toast.success(response.message)
      onComplete()
    } catch (error) {
      toast.error('Failed to assign tags')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="flex items-center gap-2 bg-muted/50 px-3 py-2 rounded-lg border border-border">
        <span className="text-sm font-medium text-foreground">
          {selectedCount} selected
        </span>
        <div className="h-4 w-px bg-border" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={loading}
              className="border-border hover:bg-accent"
            >
              <CheckCheck className="mr-2 h-4 w-4" />
              Status
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-card text-card-foreground border-border">
            <DropdownMenuLabel>Change Status</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-border" />
            <DropdownMenuItem
              onClick={handleBulkPublish}
              className="hover:bg-accent focus:bg-accent"
            >
              Publish
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleBulkUnpublish}
              className="hover:bg-accent focus:bg-accent"
            >
              Unpublish
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-border" />
            <DropdownMenuItem
              onClick={() => handleBulkStatusChange('draft')}
              className="hover:bg-accent focus:bg-accent"
            >
              Set to Draft
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleBulkStatusChange('review')}
              className="hover:bg-accent focus:bg-accent"
            >
              Set to Review
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleBulkStatusChange('scheduled')}
              className="hover:bg-accent focus:bg-accent"
            >
              Set to Scheduled
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={loading}
              className="border-border hover:bg-accent"
            >
              <FolderOpen className="mr-2 h-4 w-4" />
              Categories
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-card text-card-foreground border-border">
            <DropdownMenuLabel>Assign Categories</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-border" />
            {categories.map((category) => (
              <DropdownMenuSub key={category.id}>
                <DropdownMenuSubTrigger className="hover:bg-accent focus:bg-accent">
                  {category.name}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="bg-card border-border">
                  <DropdownMenuItem
                    onClick={() => handleBulkCategoryAssign(category.id, false)}
                    className="hover:bg-accent focus:bg-accent"
                  >
                    Add to existing
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleBulkCategoryAssign(category.id, true)}
                    className="hover:bg-accent focus:bg-accent"
                  >
                    Replace all
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={loading}
              className="border-border hover:bg-accent"
            >
              <Tag className="mr-2 h-4 w-4" />
              Tags
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-card text-card-foreground border-border">
            <DropdownMenuLabel>Assign Tags</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-border" />
            {tags.map((tag) => (
              <DropdownMenuSub key={tag.id}>
                <DropdownMenuSubTrigger className="hover:bg-accent focus:bg-accent">
                  {tag.name}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="bg-card border-border">
                  <DropdownMenuItem
                    onClick={() => handleBulkTagAssign(tag.id, false)}
                    className="hover:bg-accent focus:bg-accent"
                  >
                    Add to existing
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleBulkTagAssign(tag.id, true)}
                    className="hover:bg-accent focus:bg-accent"
                  >
                    Replace all
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="outline"
          size="sm"
          onClick={handleBulkDelete}
          disabled={loading}
          className="text-destructive border-border hover:bg-destructive/10"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </Button>
      </div>
      <ConfirmDialog />
    </>
  )
}
